/**
 * Search-engine and link-preview text: how titles and descriptions are put together, what a tag becomes, and the
 * structured data (JSON-LD) that several pages share. The same functions run on the API (which writes the page data), in
 * the build step that prerenders pages, and in the admin (which previews and checks what staff type).
 */

export const SITE_NAME = 'MeshSmith';
export const SITE_TAGLINE = 'From Mesh to Matter';

/** Google shows about 60 characters of a title and about 160 of a description before cutting them with "…". */
export const SEO_TITLE_BEST = 60;
export const SEO_DESCRIPTION_BEST = 160;
/** Under this a description is too thin to be worth showing, and the search engine may write its own. */
export const SEO_DESCRIPTION_MIN = 70;
/** A tag page is only for search engines when it has at least this many products; thinner ones are not indexed. */
export const TAG_PAGE_MIN_PRODUCTS = 3;
/** Eight or so well-chosen tags are enough; more only dilutes them. */
export const TAGS_BEST_MAX = 8;

/** Text of some HTML, on one line. */
export function plainText(html: string | null | undefined): string {
  return (html ?? '')
    .replace(/<(script|style)[\s\S]*?<\/\1>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/** Cuts at a word boundary with an ellipsis so the result is at most `max` characters. */
export function truncateAtWord(text: string, max: number): string {
  const t = text.trim();
  if (t.length <= max) return t;
  const room = t.slice(0, max - 1);
  const cut = room.replace(/\s+\S*$/, '');
  return `${(cut.length >= max * 0.6 ? cut : room).replace(/[\s,;:.\-–—]+$/, '')}…`;
}

/**
 * The text of the <title> tag: "Soy Candle Kit | MeshSmith". The brand is left off when it would push a title past what a
 * search result shows, and is never added twice.
 */
export function pageTitle(title: string, siteName = SITE_NAME): string {
  const t = title.trim();
  if (!t) return siteName;
  if (t.toLowerCase().includes(siteName.toLowerCase())) return t;
  const withBrand = `${t} | ${siteName}`;
  return withBrand.length <= SEO_TITLE_BEST + 5 ? withBrand : t;
}

/** The first of these that has any text, as plain text of at most 160 characters. Empty when none has. */
export function metaDescription(...candidates: (string | null | undefined)[]): string {
  for (const c of candidates) {
    const text = plainText(c);
    if (text) return truncateAtWord(text, SEO_DESCRIPTION_BEST);
  }
  return '';
}

/** "Hand Made" -> "hand-made": what a tag looks like in an address. */
export function tagSlug(tag: string): string {
  return tag
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** "hand made" -> "Hand Made": a tag as a heading. */
export function tagLabel(tag: string): string {
  return tag
    .trim()
    .split(/\s+/)
    .map((w) => (w ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(' ');
}

/** Tags as staff type them ("Soy, handmade , gift") -> clean, lower case, no repeats. */
export function cleanTags(tags: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of tags) {
    const t = raw.toLowerCase().replace(/\s+/g, ' ').trim();
    if (t && !seen.has(t)) {
      seen.add(t);
      out.push(t);
    }
  }
  return out;
}

export interface Crumb {
  name: string;
  /** an absolute address */
  url: string;
}

/** The trail "Home > Shop > Candles" as search engines read it. */
export function breadcrumbJsonLd(crumbs: Crumb[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({ '@type': 'ListItem', position: i + 1, name: c.name, item: c.url })),
  };
}

export type SeoLevel = 'good' | 'warn' | 'bad';
export interface SeoCheck {
  level: SeoLevel;
  /** what staff read, in plain words */
  message: string;
}

/**
 * What staff are told about a product's SEO as they type: whether the title and description will show in full, whether the
 * fall-backs are used, and whether the tags are worth having. `effectiveTitle` / `effectiveDescription` are what will
 * actually be used (the fall-back when the box is empty).
 */
export function checkProductSeo(input: {
  seoTitle: string;
  seoDescription: string;
  effectiveTitle: string;
  effectiveDescription: string;
  tags: string[];
}): SeoCheck[] {
  const checks: SeoCheck[] = [];
  const title = input.effectiveTitle.length;
  if (!input.seoTitle.trim()) checks.push({ level: 'good', message: `No SEO title written, so the product name is used: "${input.effectiveTitle}".` });
  else if (title > SEO_TITLE_BEST) checks.push({ level: 'warn', message: `The title is ${title} characters; Google shows about ${SEO_TITLE_BEST}, so the end will be cut off.` });
  else if (title < 20) checks.push({ level: 'warn', message: 'The title is very short. Say what the product is, for example "Soy Candle Making Kit".' });
  else checks.push({ level: 'good', message: `The title fits (${title} of ${SEO_TITLE_BEST} characters).` });

  const desc = input.effectiveDescription.length;
  if (!input.seoDescription.trim()) {
    checks.push(
      desc
        ? { level: 'warn', message: 'No meta description written, so the start of the short description is used. A sentence written for search results usually does better.' }
        : { level: 'bad', message: 'There is no meta description and no short description, so search engines will pick random text from the page.' },
    );
  } else if (desc < SEO_DESCRIPTION_MIN) checks.push({ level: 'warn', message: `The description is only ${desc} characters. Aim for ${SEO_DESCRIPTION_MIN} to ${SEO_DESCRIPTION_BEST}.` });
  else if (desc > SEO_DESCRIPTION_BEST) checks.push({ level: 'warn', message: `The description is ${desc} characters; Google shows about ${SEO_DESCRIPTION_BEST}, so the end will be cut off.` });
  else checks.push({ level: 'good', message: `The description fits (${desc} of ${SEO_DESCRIPTION_BEST} characters).` });

  const tags = cleanTags(input.tags).length;
  if (tags === 0) checks.push({ level: 'warn', message: 'No tags yet. Tags put the product on the matching tag page and help search and "you may also like". Add 3 to 8.' });
  else if (tags > TAGS_BEST_MAX) checks.push({ level: 'warn', message: `${tags} tags is a lot. The first ${TAGS_BEST_MAX} do the most work; extra ones dilute them.` });
  else checks.push({ level: 'good', message: `${tags} ${tags === 1 ? 'tag' : 'tags'}: each one puts this product on its tag page.` });
  return checks;
}

/** Pages that are for the customer alone. Search engines are asked to keep out of them (robots.txt), and they carry noindex. */
export const PRIVATE_PATHS = ['/admin', '/account', '/cart', '/checkout', '/order/', '/login', '/register', '/search', '/unsubscribe'] as const;

/** The robots.txt of a live site: everything is open except the private pages, and the sitemap is named. */
export function robotsTxtFor(siteUrl: string): string {
  return `User-agent: *\nAllow: /\n${PRIVATE_PATHS.map((p) => `Disallow: ${p}`).join('\n')}\n\nSitemap: ${siteUrl.replace(/\/+$/, '')}/sitemap.xml\n`;
}

export interface PageSeo {
  title: string;
  description: string;
}

/** The home page's title and description. The title carries what the shop sells, since the brand alone ranks only for itself. */
export const HOME_SEO: PageSeo = {
  title: 'MeshSmith | Craft Kits, Moulds & 3D Printing in India',
  description: 'Shop DIY craft kits, ceramic and concrete moulds, candle-making supplies and 3D printing from MeshSmith. Made for makers, shipped across India.',
};

/** The pages that are the same for everyone, by address. The storefront and the prerender step both read this one list. */
export const STATIC_SEO = {
  '/shop': { title: 'Shop all craft kits, moulds & supplies', description: 'Browse every MeshSmith product: DIY craft kits, ceramic and concrete moulds, candle-making supplies and 3D printed pieces. Ships across India.' },
  '/collections': { title: 'Collections', description: 'Curated MeshSmith collections: hand-picked kits, tools and supplies for every craft.' },
  '/new-arrivals': { title: 'New arrivals', description: 'The newest craft kits, moulds and supplies at MeshSmith, fresh from the studio.' },
  '/faq': { title: 'Frequently asked questions', description: 'Answers about MeshSmith orders, delivery, returns, products and custom work.' },
  '/contact': { title: 'Contact us', description: 'Talk to the MeshSmith studio about an order, custom work or a question: phone, WhatsApp and email.' },
  '/custom-orders': { title: 'Custom orders', description: 'Commission a custom 3D print, mould, candle or kit from MeshSmith. Tell us what you need and get a quote.' },
  '/reviews': { title: 'What makers say about MeshSmith', description: 'Reviews from people who have bought from the MeshSmith studio.' },
} as const satisfies Record<string, PageSeo>;
export type StaticSeoPath = keyof typeof STATIC_SEO;
